import React, { useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Divider,
  Stack,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import AssessmentIcon from '@mui/icons-material/Assessment';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import MapIcon from '@mui/icons-material/Map';
import PaidIcon from '@mui/icons-material/Paid';
import SportsSoccerIcon from '@mui/icons-material/SportsSoccer';
import { useAuth } from '../contexts/AuthContext';

const accent = '#64ffda';
const gold = '#ffca28';
const danger = '#ef5350';
const panel = '#242424';
const panelSoft = 'rgba(255, 255, 255, 0.035)';
const border = 'rgba(255, 255, 255, 0.09)';

const toolItems = [
  'Tabele i terminarze',
  'Podstawowe dane predykcyjne',
  'Wykresy szans i trendów',
  'Ranking Elo - szczebel centralny',
  'Ranking Elo - cała Polska',
  'Wybryki statystyczne',
  'Własne symulacje',
  'Laboratorium scenariuszy',
  'Ogólne progi punktowe',
  'Drużynowe progi punktowe',
  'Analiza playoffów',
  'Analiza trudności terminarza',
  'Kącik bukmacherski',
  'Pełne narzędzia dla lig regionalnych',
];

const priceByInterval = {
  monthly: {
    plus: { price: '29 zł / mies.', period: 'Rocznie: 250 zł' },
    regions: { price: '39 zł / mies.', period: 'Rocznie: 350 zł' },
  },
  yearly: {
    plus: { price: '250 zł / rok', period: 'Oszczędzasz 98 zł rocznie' },
    regions: { price: '350 zł / rok', period: 'Oszczędzasz 118 zł rocznie' },
  },
};

const buildPlans = (billingInterval, hasPlus, hasRegional) => [
  {
    name: 'Free',
    key: 'free',
    icon: AssessmentIcon,
    badge: 'Start',
    price: '0 zł',
    period: 'bez opłat',
    accentColor: '#b0b0b0',
    description:
      'Dla szybkiego sprawdzania lig od Ekstraklasy do III ligi bez wchodzenia w pełną analitykę.',
    cta: 'Korzystaj za darmo',
    to: '/',
    included: new Set([
      'Tabele i terminarze',
      'Podstawowe dane predykcyjne',
      'Ranking Elo - szczebel centralny',
      'Ogólne progi punktowe',
    ]),
  },
  {
    name: 'Symuliga Plus',
    key: 'plus',
    icon: SportsSoccerIcon,
    badge: 'Szczebel centralny',
    price: priceByInterval[billingInterval].plus.price,
    period: priceByInterval[billingInterval].plus.period,
    accentColor: accent,
    description:
      'Pełny warsztat analityczny od Ekstraklasy do III ligi włącznie.',
    cta: 'Wybierz Plus',
    to: `/kup-vip?plan=plus&interval=${billingInterval}`,
    included: new Set([
      'Tabele i terminarze',
      'Podstawowe dane predykcyjne',
      'Wykresy szans i trendów',
      'Ranking Elo - szczebel centralny',
      'Wybryki statystyczne',
      'Własne symulacje',
      'Laboratorium scenariuszy',
      'Ogólne progi punktowe',
      'Drużynowe progi punktowe',
      'Analiza playoffów',
      'Analiza trudności terminarza',
      'Kącik bukmacherski',
    ]),
  },
  {
    name: 'Symuliga Regionalna',
    key: 'regions',
    icon: MapIcon,
    badge: 'wszystkie ligi seniorskie',
    price: priceByInterval[billingInterval].regions.price,
    period: priceByInterval[billingInterval].regions.period,
    accentColor: gold,
    description:
      'Wszystko z Plusa oraz pełny dostęp do wszystkich seniorskich lig regionalnych aż do C Klasy.',
    cta: hasPlus && !hasRegional ? 'Rozszerz do Regionalnego' : 'Wybierz Regionalną',
    to: hasPlus && !hasRegional
      ? '/kup-vip?plan=regions&upgrade=true'
      : `/kup-vip?plan=regions&interval=${billingInterval}`,
    included: new Set([
      'Tabele i terminarze',
      'Podstawowe dane predykcyjne',
      'Wykresy szans i trendów',
      'Ranking Elo - szczebel centralny',
      'Ranking Elo - cała Polska',
      'Wybryki statystyczne',
      'Własne symulacje',
      'Laboratorium scenariuszy',
      'Ogólne progi punktowe',
      'Drużynowe progi punktowe',
      'Analiza playoffów',
      'Analiza trudności terminarza',
      'Kącik bukmacherski',
      'Pełne narzędzia dla lig regionalnych',
    ]),
  },
];

function PlanFeature({ label, included, accentColor }) {
  const Icon = included ? CheckCircleIcon : CancelIcon;

  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="flex-start"
      sx={{
        opacity: included ? 1 : 0.48,
      }}
    >
      <Icon
        sx={{
          mt: '2px',
          fontSize: 18,
          color: included ? accentColor : danger,
          flex: '0 0 auto',
        }}
      />
      <Typography
        variant="body2"
        sx={{
          color: included ? 'text.secondary' : 'rgba(255, 255, 255, 0.46)',
          lineHeight: 1.35,
          textDecoration: included ? 'none' : 'line-through',
          textDecorationColor: 'rgba(239, 83, 80, 0.55)',
        }}
      >
        {label}
      </Typography>
    </Stack>
  );
}

function PlanCard({ plan, highlighted = false, active = false }) {
  const Icon = plan.icon;
  const isRegional = plan.name === 'Symuliga Regionalna';
  const disabled = active || plan.disabled;
  const ctaLabel = active ? 'Pakiet aktywny' : plan.cta;

  return (
    <Paper
      elevation={0}
      sx={{
        width: '100%',
        height: 'auto',
        p: { xs: 2.25, md: 2.5 },
        borderRadius: 2,
        backgroundColor: highlighted ? 'rgba(100, 255, 218, 0.045)' : panelSoft,
        border: `1px solid ${highlighted ? 'rgba(100, 255, 218, 0.35)' : border}`,
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <Stack spacing={2}>
        <Box sx={{ minHeight: { md: 230 } }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                width: 42,
                height: 42,
                borderRadius: 1,
                display: 'grid',
                placeItems: 'center',
                backgroundColor: `${plan.accentColor}14`,
                border: `1px solid ${plan.accentColor}33`,
                flex: '0 0 auto',
              }}
            >
              <Icon sx={{ color: plan.accentColor, fontSize: 24 }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h6" sx={{ color: '#fff', fontWeight: 900, lineHeight: 1.15 }}>
                {plan.name}
              </Typography>
              <Chip
                label={plan.badge}
                size="small"
                sx={{
                  mt: 0.75,
                  height: 22,
                  borderRadius: 1,
                  color: plan.accentColor,
                  backgroundColor: `${plan.accentColor}12`,
                  border: `1px solid ${plan.accentColor}26`,
                  fontWeight: 800,
                }}
              />
            </Box>
          </Stack>

          <Box sx={{ mt: 2 }}>
            <Typography variant="h5" sx={{ color: '#fff', fontWeight: 900 }}>
              {plan.price}
            </Typography>
            <Typography variant="body2" sx={{ color: plan.accentColor, fontWeight: 800 }}>
              {plan.period}
            </Typography>
          </Box>

          <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary', lineHeight: 1.55 }}>
            {plan.description}
          </Typography>
        </Box>

        <Divider sx={{ borderColor: border }} />

        <Stack spacing={1.1}>
          {toolItems.map((item) => (
            <PlanFeature
              key={item}
              label={item}
              included={plan.included.has(item)}
              accentColor={plan.accentColor}
            />
          ))}
        </Stack>

        <Button
          variant={highlighted ? 'contained' : 'outlined'}
          size="large"
          {...(!disabled ? { component: RouterLink, to: plan.to } : {})}
          disabled={disabled}
          sx={{
            mt: 2,
            py: 1.15,
            borderRadius: 1,
            color: highlighted || isRegional ? '#101010' : '#fff',
            backgroundColor: highlighted || isRegional ? plan.accentColor : 'transparent',
            borderColor: highlighted || isRegional ? plan.accentColor : border,
            fontWeight: 900,
            textTransform: 'none',
            '&:hover': {
              backgroundColor: highlighted
                ? '#4be6c4'
                : isRegional
                  ? '#ffb300'
                  : `${plan.accentColor}12`,
              borderColor: plan.accentColor,
            },
            '&.Mui-disabled': {
              color: 'rgba(16, 16, 16, 0.72)',
              backgroundColor: 'rgba(255, 202, 40, 0.42)',
              borderColor: 'rgba(255, 202, 40, 0.18)',
            },
          }}
        >
          {ctaLabel}
        </Button>
      </Stack>
    </Paper>
  );
}

export default function VipTeaser() {
  const { hasRole } = useAuth();
  const [billingInterval, setBillingInterval] = useState('monthly');
  const hasPlus = hasRole('admin') || hasRole('poland') || hasRole('poland_regions');
  const hasRegional = hasRole('admin') || hasRole('poland_regions');
  const plans = useMemo(
    () => buildPlans(billingInterval, hasPlus, hasRegional),
    [billingInterval, hasPlus, hasRegional]
  );

  const handleIntervalChange = (event, nextInterval) => {
    if (nextInterval) {
      setBillingInterval(nextInterval);
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        maxWidth: 1120,
        mx: 'auto',
        my: { xs: 3, md: 6 },
        borderRadius: 2,
        overflow: 'visible',
        backgroundColor: panel,
        color: '#fff',
        border: `1px solid ${border}`,
        boxShadow: '0 10px 30px rgba(0, 0, 0, 0.35)',
      }}
    >
      <Box
        sx={{
          p: { xs: 2.5, md: 4 },
          backgroundColor: '#1f1f1f',
          borderBottom: `1px solid ${border}`,
        }}
      >
        <Stack spacing={1.5} alignItems="center" textAlign="center">
          <Chip
            icon={<PaidIcon sx={{ color: `${gold} !important` }} />}
            label="Pakiety dostępu"
            sx={{
              borderRadius: 1,
              color: '#fff',
              backgroundColor: 'rgba(255, 202, 40, 0.1)',
              border: '1px solid rgba(255, 202, 40, 0.22)',
              fontWeight: 800,
            }}
          />
          <Typography
            variant="h4"
            component="h2"
            sx={{
              fontWeight: 900,
              color: '#fff',
              fontSize: { xs: '1.55rem', md: '2.15rem' },
              lineHeight: 1.15,
            }}
          >
            Wybierz poziom analityki Symuligi
          </Typography>
          <Typography
            variant="body1"
            sx={{
              maxWidth: 760,
              color: 'text.secondary',
              lineHeight: 1.65,
            }}
          >
            Podstawowe informacje zostają dostępne za darmo, a pakiety płatne
            odblokowują pełne narzędzia do analizy lig, scenariuszy i playoffów.
          </Typography>
          <ToggleButtonGroup
            exclusive
            value={billingInterval}
            onChange={handleIntervalChange}
            size="small"
            sx={{
              mt: 1,
              p: 0.35,
              borderRadius: 1,
              backgroundColor: 'rgba(255, 255, 255, 0.05)',
              border: `1px solid ${border}`,
              '& .MuiToggleButton-root': {
                minWidth: 118,
                border: 0,
                borderRadius: '6px !important',
                color: 'rgba(255, 255, 255, 0.72)',
                fontWeight: 900,
                textTransform: 'none',
              },
              '& .Mui-selected': {
                color: '#101010 !important',
                backgroundColor: `${accent} !important`,
              },
            }}
          >
            <ToggleButton value="monthly">Miesięcznie</ToggleButton>
            <ToggleButton value="yearly">Rocznie</ToggleButton>
          </ToggleButtonGroup>
        </Stack>
      </Box>

      <Box sx={{ p: { xs: 2.5, md: 4 }, pb: { xs: 3.25, md: 4.75 } }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
            gap: { xs: 3.25, md: 2.25 },
            alignItems: 'stretch',
          }}
        >
          {plans.map((plan) => (
            <Box key={plan.name} sx={{ minWidth: 0, display: 'flex' }}>
              <PlanCard
                plan={plan}
                highlighted={plan.name === 'Symuliga Plus'}
                active={(plan.key === 'plus' && hasPlus) || (plan.key === 'regions' && hasRegional)}
              />
            </Box>
          ))}
        </Box>
      </Box>
    </Paper>
  );
}
